import api from '@/api';
import { userDocumentSearchCache } from '@/apiCache';
import { StorageDetails, UseStorage } from '@/utils/user-document/storage';

/** 連携状態のポーリング間隔 */
const BOX_CONNECT_STATUS_POLLING_INTERVAL = 5 * 1000;
/** 連携失敗とみなすポーリング時間 */
const BOX_CONNECT_STATUS_POLLING_TIMEOUT = 3 * 60 * 1000;

const resetToNotConnected = (item: StorageDetails) => {
  item.isConnected = false;
  item.isLoaded = false;
  item.status = 'NOT_CONNECTED';
  item.fileCount = '-';
  item.syncStartedAt = '-';
  item.syncEndedAt = '-';
};

const openAuthorizePage = (item: StorageDetails) => {
  resetToNotConnected(item);
  const clientId = import.meta.env.VITE_BOX_CLIENT_ID;
  const redirectUri = `${location.origin}/api/v1/admin/box_api_token_callback`;
  const authorizeUrl = `https://account.box.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
  window.open(authorizeUrl, '_blank');
  startPollingBoxStatus(item);
};

const connect = (item: StorageDetails) => {
  openAuthorizePage(item);
};

const isConnected = async () => (await api.headTeamBoxApiToken()) === 200;

const startPollingBoxStatus = (item: StorageDetails) => {
  // 連携状態をポーリングし、連携成功したら状態を変更
  const intervalId = setInterval(async () => {
    if (await isConnected()) {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      updateDetails(item);
    }
  }, BOX_CONNECT_STATUS_POLLING_INTERVAL);

  // 指定時間経過しても連携が成功しなかった場合、失敗とみなす
  const timeoutId = setTimeout(async () => {
    clearInterval(intervalId);
    if (!(await isConnected())) {
      item.status = 'CONNECT_FAILED';
      // OAuth リダイレクト後は isLoaded = false にしてボタンを無効化しているため、
      // タイムアウト後は再試行できるよう isLoaded = true に戻す
      item.isLoaded = true;
    }
  }, BOX_CONNECT_STATUS_POLLING_TIMEOUT);
};

const disconnect = (item: StorageDetails) => {
  return async () => {
    // 連携情報を削除
    await api.deleteTeamBoxApiToken();
    await updateDetails(item);
    // 認証情報を削除後、検索できなくなるため、社内情報に関するキャッシュをクリア
    userDocumentSearchCache.cleanup();
  };
};

const reauthorize = async (item: StorageDetails) => {
  openAuthorizePage(item);
};

const updateDetails = async (item: StorageDetails) => {
  if (await isConnected()) {
    item.isConnected = true;
    try {
      const status = await api.getTeamStorageStatus('box');
      switch (status.status) {
        case 'syncing':
          item.status = 'SYNCING';
          break;
        case 'synced':
          item.status = 'SYNCED';
          break;
      }
      item.fileCount = status.file_count?.toLocaleString() ?? '-';
      item.syncStartedAt = status.sync_started_at ?? '-';
      item.syncEndedAt = status.sync_ended_at ?? '-';
    } catch {
      item.status = 'FETCH_ERROR';
      item.fileCount = '-';
      item.syncStartedAt = '-';
      item.syncEndedAt = '-';
      return;
    }
  } else {
    item.isConnected = false;
    item.status = 'NOT_CONNECTED';
    item.fileCount = '-';
    item.syncStartedAt = '-';
    item.syncEndedAt = '-';
  }
  item.isLoaded = true;
};

export interface UseBox extends UseStorage {
  reauthorize: (item: StorageDetails) => Promise<void>;
}

export const useBox: () => UseBox = () => ({
  connect,
  disconnect,
  updateDetails,
  reauthorize,
});
